import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { Review } from './types';
import { averageRating, clampRating, formatRating, ratingDistribution, reviewCountLabel, sortNewestFirst } from './format';
import { Stars } from './Stars';
import { StoreImage } from '../../ui/StoreImage';

interface Props {
  open: boolean;
  onClose: () => void;
  reviews: Review[];
}

/**
 * Recensionsvyn. Öppnas ovanpå produktsidan (ingen ny sida).
 * Överst: sammanfattning per antal stjärnor. Därunder: alla recensioner, scrollbara.
 * Stängs med krysset, Escape eller ett klick utanför.
 */
export function ReviewsSheet({ open, onClose, reviews }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
      // Fokus stannar i vyn (aria-modal): Tab från sista går till första och tvärtom.
      if (e.key === 'Tab' && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>('button, [href], video[controls], [tabindex]:not([tabindex="-1"])');
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
      previousFocus?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const list = sortNewestFirst(reviews);
  const dist = ratingDistribution(reviews);
  const avg = averageRating(reviews);

  return createPortal(
    <div className="cn-sheet" onClick={onClose}>
      <div ref={panelRef} className="cn-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="cn-sheet-title" onClick={(e) => e.stopPropagation()}>
        <header className="cn-sheet__head">
          <h2 id="cn-sheet-title">Recensioner</h2>
          <button ref={closeRef} type="button" className="cn-sheet__close" aria-label="Stäng recensionerna" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>

        <div className="cn-sheet__scroll">
          <div className="cn-summary">
            <div className="cn-summary__score">
              <span className="cn-summary__avg">{formatRating(avg)}</span>
              <Stars rating={avg} size={18} />
              <span className="cn-summary__count">Baserat på {reviewCountLabel(reviews.length)}</span>
            </div>
            <ul className="cn-summary__bars">
              {dist.map((d) => (
                <li key={d.stars} className="cn-bar" aria-label={`${d.stars} stjärnor: ${d.count}`}>
                  <span className="cn-bar__label" aria-hidden="true">{d.stars}</span>
                  <span className="cn-bar__track" aria-hidden="true">
                    <span className="cn-bar__fill" style={{ width: `${Math.round(d.share * 100)}%` }} />
                  </span>
                  <span className="cn-bar__count" aria-hidden="true">{d.count}</span>
                </li>
              ))}
            </ul>
          </div>

          <ul className="cn-review-list">
            {list.map((r) => (
              <li key={r.id} className="cn-review">
                <div className="cn-review__head">
                  {r.avatarUrl ? (
                    <StoreImage className="cn-avatar" src={r.avatarUrl} width={128} alt="" loading="lazy" decoding="async" />
                  ) : (
                    <span className="cn-avatar cn-avatar--initial" aria-hidden="true">{r.name.trim().charAt(0).toUpperCase()}</span>
                  )}
                  <div>
                    <p className="cn-review__name">{r.name}</p>
                    <Stars rating={clampRating(r.rating)} size={14} />
                  </div>
                </div>
                {r.text && <p className="cn-review__text">{r.text}</p>}
                {r.media && r.media.length > 0 && (
                  <div className="cn-review__media">
                    {r.media.slice(0, 4).map((m) =>
                      m.type === 'video' ? (
                        <video key={m.url} src={m.url} poster={m.posterUrl ?? undefined} controls playsInline preload="metadata" />
                      ) : (
                        <StoreImage key={m.url} src={m.url} width={256} alt={`Bild från ${r.name}`} loading="lazy" decoding="async" />
                      ),
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>,
    document.body,
  );
}
